import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LayersIcon from '@mui/icons-material/Layers';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddIcon from '@mui/icons-material/Add';

import ReportIcon from '@mui/icons-material/Report';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PublicIcon from '@mui/icons-material/Public';
import ListIcon from '@mui/icons-material/List';

const menuSections = [
  {
    header: null,
    items: [
      { title: 'Dashboard', icon: <DashboardIcon />, path: '/' },
      { title: 'Location', icon: <LocationOnIcon />, path: '/location' },
      { title: 'Client', icon: <BusinessIcon />, path: '/clients' },
      { title: 'Phase', icon: <TimelineIcon />, path: '/phase' },
      { title: 'Tasks', icon: <AssignmentIcon />, path: '/tasks' },
      {
        title: 'Project',
        icon: <ListAltIcon />,
        subMenu: [
          { label: 'Add New Project', path: '/project/add', icon: <AddIcon /> },
          { label: 'Project List', path: '/project/list', icon: <ListIcon /> },
        ],
      },
      { title: 'Processes', icon: <GroupWorkIcon />, path: '/processes' },
      { title: 'Employee Project Engagement', icon: <WorkspacesIcon />, path: '/employee-engagement' },
    ],
  },
  {
    header: 'MANAGEMENT',
    items: [
      {
        title: 'Resource Mapping',
        icon: <AccountTreeIcon />,
        highlight: true,
        path: '/resource-mapping',
      },
      {
        title: 'Levels',
        icon: <LayersIcon />,
        subMenu: [
          { label: 'Add Level', path: '/levels/add', icon: <AddIcon /> },
          { label: 'Level List', path: '/levels/list', icon: <ListIcon /> },
        ],
      },
      {
        title: 'Department',
        icon: <ApartmentIcon />,
        subMenu: [
          { label: 'Add Department', path: '/departments/add', icon: <AddIcon /> },
          { label: 'Department List', path: '/departments/list', icon: <ListIcon /> },
        ],
      },
      {
        title: 'Users',
        icon: <PeopleAltIcon />,
        subMenu: [
          { label: 'Add User', path: '/users/add', icon: <PersonAddIcon /> },
          { label: 'User List', path: '/users/list', icon: <ListIcon /> },
        ],
      },
      {
        title: 'User Report',
        icon: <AssessmentIcon />,
        subMenu: [
          { label: 'User Weekly Report', path: '#', icon: <ReportIcon /> },
          { label: 'Master Data', path: '#', icon: <DataObjectIcon /> },
          { label: 'Master Data ($, Offshore)', path: '#', icon: <PublicIcon /> },
          { label: 'Master Data ($, Onshore)', path: '#', icon: <PublicIcon /> },
        ],
      },
      {
        title: 'Email Configuration',
        icon: <EmailIcon />,
        path: '/email-configuration',
      },
      { title: 'Application Settings', icon: <SettingsIcon />, path: '/settings' },
      { title: 'Logout', icon: <ExitToAppIcon />, path: '/logout' },
    ],
  },
];

const Sidebar = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  // Dynamic project settings state
  const [projectSettings, setProjectSettings] = useState({ project_logo_url: '', project_name: '' });

  useEffect(() => {
    // Fetch project settings on mount
    fetch('/api/project-settings')
      .then(res => res.json())
      .then(data => {
        setProjectSettings({
          project_logo_url: data?.project_logo_url || process.env.PUBLIC_URL + '/logo.png',
          project_name: data?.project_name || 'VOne PM',
        });
      });
  }, []);

  const logoUrl = projectSettings.project_logo_url || process.env.PUBLIC_URL + '/logo.png';
  const appName = projectSettings.project_name || 'VOne PM';

  const handleMenuClick = idx => {
    setOpenMenu(openMenu === idx ? null : idx);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Helper to check if a menu or submenu is active
  const isActive = (path) => path && location.pathname === path;

  return (
    <>
      <button
        className="sidebar-hamburger"
        aria-label="Open menu"
        aria-controls="sidebar-nav"
        aria-expanded={drawerOpen}
        onClick={handleDrawerToggle}
      >
        <span className="hamburger-bar" />
        <span className="hamburger-bar" />
        <span className="hamburger-bar" />
      </button>
      <nav
        id="sidebar-nav"
        className={`sidebar-drawer${drawerOpen ? ' open' : ''}`}
        aria-label="Main menu"
      >
        <div className="sidebar-header">
          <img src={logoUrl} alt="Company Logo" className="sidebar-logo" />
          <span className="sidebar-title">{appName}</span>
        </div>
        <div className="sidebar-scroll">
          {menuSections.map((section, sidx) => (
            <div key={sidx}>
              {section.header && (
                <div className="sidebar-section-header">{section.header}</div>
              )}
              <ul className="sidebar-list">
                {section.items.map((menu, idx) => (
                  <li key={menu.title} className="sidebar-menu-item" style={{ listStyle: 'none' }}>
                    {menu.subMenu ? (
                      <button
                        className={`sidebar-menu-title${menu.highlight ? ' highlight' : ''}`}
                        aria-expanded={openMenu === menu.title}
                        aria-controls={`submenu-${menu.title}`}
                        onClick={() => handleMenuClick(menu.title)}
                        style={openMenu === menu.title ? { background: '#e3f0fc', color: '#1976d2', fontWeight: 'bold' } : {}}
                      >
                        <span className="sidebar-icon">{menu.icon}</span>
                        {menu.title}
                        <span className={`arrow${openMenu === menu.title ? ' open' : ''}`}>▶</span>
                      </button>
                    ) : (
                      <Link
                        to={menu.path || '#'}
                        className={`sidebar-menu-title${isActive(menu.path) ? ' active' : ''}`}
                        tabIndex={0}
                        style={isActive(menu.path) ? { background: '#e3f0fc', color: '#1976d2', fontWeight: 'bold' } : {}}
                      >
                        <span className="sidebar-icon">{menu.icon}</span>
                        {menu.title}
                      </Link>
                    )}
                    {menu.subMenu && openMenu === menu.title && (
                      <ul className="sidebar-submenu" style={{ listStyle: 'none' }} id={`submenu-${menu.title}`}>
                        {menu.subMenu.map(sub => (
                          <li key={sub.label} style={{ listStyle: 'none' }}>
                            <Link
                              to={sub.path}
                              className={`sidebar-submenu-link${isActive(sub.path) ? ' active' : ''}${menu.highlight ? ' highlight' : ''}`}
                              tabIndex={0}
                              style={isActive(sub.path) ? { background: '#e3f0fc', color: '#1976d2', fontWeight: 'bold' } : {}}
                            >
                              <span className="sidebar-icon">{sub.icon}</span>
                              {sub.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>
      <style>{`
        .sidebar-header {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1.35rem;
          font-weight: 700;
          padding: 28px 28px 18px 28px;
          letter-spacing: 1px;
          background: #fff;
          border-bottom: 1px solid #e3f0fc;
        }
        .sidebar-logo {
          width: 38px;
          height: 38px;
          object-fit: contain;
          border-radius: 8px;
          background: #fff;
          border: 1px solid #e0e0e0;
        }
        .sidebar-title {
          color: #1976d2;
        }
        .sidebar-section-header {
          font-size: 0.78rem;
          font-weight: 700;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 18px 28px 6px 28px;
        }
        .sidebar-scroll {
          overflow-y: auto;
          height: calc(100vh - 80px);
          background: #f8f9fa;
        }
        .sidebar-drawer {
          width: 260px;
          background: #f8f9fa;
          color: #222;
          height: 100vh;
          position: fixed;
          left: 0;
          top: 0;
          z-index: 1200;
          box-shadow: 2px 0 12px rgba(0,0,0,0.04);
          transform: translateX(0);
          transition: transform 0.3s cubic-bezier(.4,0,.2,1);
          display: flex;
          flex-direction: column;
        }
        .sidebar-drawer:not(.open) {
          transform: translateX(-100%);
        }
        @media (max-width: 900px) {
          .sidebar-drawer {
            transform: translateX(-100%);
          }
          .sidebar-drawer.open {
            transform: translateX(0);
          }
          .sidebar-hamburger {
            display: flex;
          }
        }
        @media (min-width: 900px) {
          .sidebar-drawer {
            transform: translateX(0) !important;
          }
          .sidebar-hamburger {
            display: none !important;
          }
        }
        .sidebar-list {
          list-style: none;
          padding: 0;
          margin: 0;
          flex: 1;
        }
        .sidebar-menu-item {
          margin-bottom: 2px;
        }
        .sidebar-menu-title, .sidebar-menu-title:focus, .sidebar-menu-title:hover, .sidebar-menu-title.active {
          text-decoration: none !important;
          width: 100%;
          text-align: left;
          background: none;
          border: none;
          color: #222;
          font-size: 1.08rem;
          font-weight: 600;
          padding: 12px 28px;
          cursor: pointer;
          outline: none;
          transition: background 0.15s, color 0.15s;
          display: flex;
          align-items: center;
          border-radius: 8px;
        }
        .sidebar-menu-title.active, .sidebar-menu-title.highlight, .sidebar-menu-title:focus {
          background: #e3f0fc;
          color: #1976d2;
          font-weight: bold;
        }
        .sidebar-submenu {
          background: #f8f9fa;
          padding: 0 0 0 18px;
        }
        .sidebar-submenu li {
          margin: 0;
        }
        .sidebar-submenu-link, .sidebar-submenu-link:focus, .sidebar-submenu-link:hover, .sidebar-submenu-link.active {
          text-decoration: none !important;
          display: flex;
          align-items: center;
          padding: 10px 18px;
          color: #444;
          border-radius: 6px;
          background: #fff;
          margin-bottom: 2px;
          transition: background 0.15s, color 0.15s;
        }
        .sidebar-submenu-link.active, .sidebar-submenu-link.highlight, .sidebar-submenu-link:focus {
          background: #e3f0fc;
          color: #1976d2;
          font-weight: bold;
        }
        .sidebar-submenu-link:hover, .sidebar-menu-title:hover {
          background: #e3f0fc;
          color: #1976d2;
        }
        .sidebar-icon {
          margin-right: 12px;
          vertical-align: middle;
          font-size: 1.2em;
          color: #1976d2;
        }
        .arrow {
          float: right;
          margin-left: auto;
          font-size: 0.9em;
          transition: transform 0.2s;
          color: #1976d2;
        }
        .arrow.open {
          transform: rotate(90deg);
        }
        .sidebar-hamburger {
          display: none;
          position: fixed;
          top: 18px;
          left: 18px;
          z-index: 1201;
          background: #fff;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          width: 44px;
          height: 44px;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.07);
        }
        .hamburger-bar {
          display: block;
          width: 26px;
          height: 3px;
          margin: 4px auto;
          background: #222;
          border-radius: 2px;
        }
        @media (max-width: 600px) {
          .sidebar-drawer {
            width: 90vw;
            min-width: 0;
          }
          .sidebar-header {
            font-size: 1.1rem;
            padding: 18px 18px 12px 18px;
          }
          .sidebar-menu-title {
            padding: 10px 18px;
            font-size: 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default Sidebar;